import { IconAlertCircle, IconCheck, IconInfo } from '@developerz/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { createDonation } from '../lib/donations';
import { createMutation } from '../lib/mutation';
import { Button } from './ui/Button';

// Step 2 of the donate wizard (arc 101 slice 11, plan doc 05): the mandatory
// $50/mo fee. ONE option — there is nothing to configure, and a price picker
// would imply tiers we do not sell.
//
// What the fee buys is stated plainly: we operate the donor's own box and key as
// a worker on the project they picked. It is never a payment to the maintainer,
// and this copy is the only place a donor learns that, so it must not soften
// into "support the project financially".
//
// A 2xx here is an INTENT to buy. The gift comes back `active: false` with its
// `checkout_url`, and the subscription becomes real only when Stripe's webhook
// reaches the worker — which is why this step reports `funded` from the server's
// own answer (passed in) and never from the `?donated=1` it was redirected with.

export type DonatePayStepProps = {
  readonly project: string;
  readonly token: string | null;
  /** The server says the donor subscription is live — the ONLY unlock signal. */
  readonly funded: boolean;
  /** A `money` gift for this project exists but Stripe has not confirmed it yet. */
  readonly pending: boolean;
  /** Refetch the gift list after a create (the row, not this echo, is the truth). */
  readonly onCreated: () => void;
};

export function DonatePayStep(props: DonatePayStepProps): JSX.Element {
  // A create landed and the browser stayed here, so a PENDING row now exists.
  // The create has no uniqueness guard — distinct gifts get distinct sessions,
  // by design — so a second Start would pledge the same project twice.
  const [recorded, setRecorded] = createSignal(false);
  // A SUCCESS that still has something to say, so it cannot live in the
  // mutation's `error()` — that one is cleared at the start of every attempt and
  // written only by a rejection.
  const [noCheckout, setNoCheckout] = createSignal<string | null>(null);

  const pay = createMutation({
    call: () => {
      setNoCheckout(null);
      return createDonation(
        { kind: 'money', target: 'project', repo_full_name: props.project },
        props.token,
      );
    },
    fallback: t('donations.pay_error'),
    onSuccess: (created) => {
      props.onCreated();
      if (typeof created.checkout_url === 'string') {
        window.location.assign(created.checkout_url);
        return;
      }
      setRecorded(true);
      // A money gift ALWAYS carries a hosted session (a deployment that mints
      // none refuses the gift outright — 101 sweep OSS-3), so a body without one
      // is a body this client cannot read. Going quiet would leave the donor on
      // an unchanged screen with a pledge they were never told was recorded.
      setNoCheckout(t('donations.pay_no_checkout'));
    },
  });
  const busy = pay.pending;
  const error = (): string | null => pay.error() ?? noCheckout();
  const start = (): void => void pay.run();

  return (
    <div class="field-group">
      <Show when={props.funded} fallback={<p class="field__desc">{t('donations.pay_what')}</p>}>
        <div class="alert alert--icon" role="status">
          <IconCheck />
          <p class="alert__title">{t('donations.pay_active')}</p>
          <p class="alert__desc">{t('donations.pay_park_warning')}</p>
        </div>
      </Show>

      <Show when={!props.funded}>
        <div>
          <p class="metric-grid__label">{t('donations.step_pay')}</p>
          <p class="metric-grid__value">{t('donations.pay_price')}</p>
        </div>
        {/* Said BEFORE they subscribe, not after: a cancelled subscription parks
            every resource they pinned, and discovering that later reads as a
            silently dead box. */}
        <div class="alert alert--icon" role="note">
          <IconInfo />
          <p class="alert__title">{t('donations.pay_park_warning')}</p>
        </div>
      </Show>

      <Show when={!props.funded && props.pending}>
        <div class="alert alert--icon" role="status">
          <IconInfo />
          <p class="alert__title">{t('donations.pay_pending')}</p>
        </div>
      </Show>

      <Show when={error()}>
        <div class="alert alert--icon alert--destructive" role="alert">
          <IconAlertCircle />
          <p class="alert__title">{error()}</p>
        </div>
      </Show>

      {/* Retired once a pledge is on the books: beside a notice saying the gift
          was recorded, a live Start reads as the way to finish, and every press
          mints another pending row for the same project. "On the books" is the
          SERVER's `pending` (a pledge from an earlier visit, e.g. an abandoned
          checkout) as well as this mount's own `recorded` — the local flag alone
          is false on every mount (plan 101 slice 17 row 17-8). The way out of
          an abandoned pledge is "Cancel pledge" on /donations. */}
      <Show when={!props.funded && !props.pending && !recorded()}>
        <div class="dialog__footer">
          <Button disabled={busy()} onClick={start}>
            {t(busy() ? 'donations.pay_starting' : 'donations.pay_cta')}
          </Button>
        </div>
      </Show>
    </div>
  );
}
